export type CompetitionHandoffKind = 'pair' | 'claim';

export interface CompetitionHandoff {
  kind: CompetitionHandoffKind;
  token: string;
}

const sessionPrefix = 'tark:competition-handoff';
const pairPattern = /^[A-Za-z0-9_-]{43,128}$/;
const claimPattern = /^[A-Za-z0-9_-]{32,256}$/;

function storageKey(slug: string, kind: CompetitionHandoffKind): string {
  return `${sessionPrefix}:${slug}:${kind}`;
}

function isValid(kind: CompetitionHandoffKind, token: string): boolean {
  return (kind === 'pair' ? pairPattern : claimPattern).test(token);
}

function readStored(slug: string): CompetitionHandoff | null {
  try {
    for (const kind of ['pair', 'claim'] as const) {
      const token = window.sessionStorage.getItem(storageKey(slug, kind));
      if (token && isValid(kind, token)) {
        return { kind, token };
      }
    }
  } catch {
    return null;
  }
  return null;
}

export function captureCompetitionHandoff(slug: string): CompetitionHandoff | null {
  if (typeof window === 'undefined' || !slug) {
    return null;
  }

  const fragment = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  const candidate = (['pair', 'claim'] as const)
    .map((kind) => ({ kind, token: fragment.get(kind)?.trim() || '' }))
    .find(({ kind, token }) => token && isValid(kind, token));

  if (fragment.has('pair') || fragment.has('claim')) {
    window.history.replaceState(
      window.history.state,
      '',
      `${window.location.pathname}${window.location.search}`
    );
  }

  if (candidate) {
    try {
      window.sessionStorage.setItem(storageKey(slug, candidate.kind), candidate.token);
    } catch {
      // The current render can still complete the handoff when storage is unavailable.
    }
    return candidate;
  }

  return readStored(slug);
}

export function clearCompetitionHandoff(slug: string, kind: CompetitionHandoffKind): void {
  try {
    window.sessionStorage.removeItem(storageKey(slug, kind));
  } catch {
    // Nothing else needs to happen after a successful handoff.
  }
}

export function createCompetitionSecret(): string {
  const bytes = new Uint8Array(32);
  window.crypto.getRandomValues(bytes);
  let raw = '';
  bytes.forEach((byte) => {
    raw += String.fromCharCode(byte);
  });
  return window.btoa(raw).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

export function createPairInviteUrl(slug: string, token: string): string {
  return `${window.location.origin}/competitions/${encodeURIComponent(slug)}#pair=${encodeURIComponent(token)}`;
}

export function createSafeCompetitionReturnPath(slug: string, divisionPublicId?: string): string {
  const query = new URLSearchParams();
  if (divisionPublicId) {
    query.set('division', divisionPublicId);
  }
  return `/competitions/${encodeURIComponent(slug)}${query.size ? `?${query.toString()}` : ''}`;
}

export async function shareOrCopyCompetitionLink(url: string, title: string): Promise<'shared' | 'copied'> {
  if (navigator.share) {
    try {
      await navigator.share({ title, url });
      return 'shared';
    } catch (error) {
      if ((error as Error)?.name !== 'AbortError') {
        // Copy fallback below.
      } else {
        return 'shared';
      }
    }
  }
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(url);
  } else {
    const input = document.createElement('textarea');
    input.value = url;
    input.setAttribute('readonly', '');
    input.style.position = 'fixed';
    input.style.opacity = '0';
    document.body.appendChild(input);
    input.select();
    document.execCommand('copy');
    document.body.removeChild(input);
  }
  return 'copied';
}
